/**
 * Data layer for `cordova-ionic-sdk`. Thin typed fetch wrappers over
 * /api/v1/cordova-ionic-sdk. Raw `fetch()` is confined to this file
 * per the dash contract (D6).
 *
 * Backend endpoints:
 *   POST /register      — idempotent install registration
 *   POST /event         — idempotent event ingest
 *   GET  /installs/:id  — fetch install by install_id
 *   GET  /              — list installs (latest first, max 50)
 */

export interface CordovaInstall {
	id: string;
	tenant_id: string;
	install_id: string;
	app_id: string;
	sdk_version: string;
	platform: string;
	plugin_version: string;
	created_at: number;
}

export interface ListInstallsResponse {
	items: CordovaInstall[];
}

export interface RegisterInstallBody {
	install_id: string;
	app_id: string;
	sdk_version: string;
	platform: "ios" | "android" | "web";
	plugin_version: string;
}

export interface RegisterInstallResponse {
	ok: true;
	install: CordovaInstall | null;
}

export interface IngestEventBody {
	install_id: string;
	event_id: string;
	name: string;
	payload?: unknown;
}

export interface IngestEventResponse {
	ok: true;
}

export interface FetchInstallResponse {
	install: CordovaInstall;
}

const BASE = "/api/v1/cordova-ionic-sdk";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listInstalls(): Promise<ListInstallsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListInstallsResponse>(res);
}

export async function registerInstall(
	body: RegisterInstallBody,
): Promise<RegisterInstallResponse> {
	const res = await fetch(`${BASE}/register`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<RegisterInstallResponse>(res);
}

export async function ingestEvent(
	body: IngestEventBody,
): Promise<IngestEventResponse> {
	const res = await fetch(`${BASE}/event`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<IngestEventResponse>(res);
}

export async function fetchInstall(
	installId: string,
): Promise<FetchInstallResponse> {
	const res = await fetch(
		`${BASE}/installs/${encodeURIComponent(installId)}`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<FetchInstallResponse>(res);
}

export const cordovaIonicSdkQueryKeys = {
	all: ["cordova-ionic-sdk"] as const,
	list: () => [...cordovaIonicSdkQueryKeys.all] as const,
	detail: (installId: string) =>
		[...cordovaIonicSdkQueryKeys.all, installId] as const,
};
